import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import PageCard from '../service/components/PageCard';
import DefaultModal from '../service/components/modals/DefaultModal';
import LoadingModal from '../service/components/modals/LoadingModal';
import ProgramaSelector from '../components/ProgramaSelector';

function ContaProgramaManagement() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const cnpj = state?.cnpj || '';
  const numConta = state?.numConta || '';
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!cnpj || !numConta) {
      navigate('/contas');
    }
  }, [cnpj, numConta, navigate]);

  const [conta, setConta] = useState(null);
  const [programasAtivos, setProgramasAtivos] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newIdPrograma, setNewIdPrograma] = useState('');
  const [newDataInicio, setNewDataInicio] = useState('');

  const [editingPrograma, setEditingPrograma] = useState(null);
  const [editDataInicio, setEditDataInicio] = useState('');

  const [modalData, setModalData] = useState({ show: false, type: '', message: '' });

  const showModal = (type, message) => setModalData({ show: true, type, message });
  const closeModal = () => setModalData(prev => ({ ...prev, show: false }));

  const buscarConta = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const contaUrl = `${API_BASE_URL.CONTA}/${encodeURIComponent(cnpj)}/${encodeURIComponent(numConta)}`;
      const progUrl = `${API_BASE_URL.CONTA_PROGRAMA}/${encodeURIComponent(cnpj)}/${encodeURIComponent(numConta)}/atual`;
      const histUrl = `${API_BASE_URL.CONTA_PROGRAMA}/${encodeURIComponent(cnpj)}/${encodeURIComponent(numConta)}/historico`;

      const [contaRes, progRes, histRes] = await Promise.all([
        axios.get(contaUrl, { headers }),
        axios.get(progUrl, { headers }).catch(() => null),
        axios.get(histUrl, { headers }).catch(() => ({ data: [] })),
      ]);

      setConta(contaRes.data);
      setProgramasAtivos(progRes?.status === 200 ? (Array.isArray(progRes.data) ? progRes.data : []) : []);
      setHistorico(Array.isArray(histRes.data) ? histRes.data : []);
    } catch (error) {
      if (error.response?.status === 404) {
        showModal('error', 'Conta não encontrada.');
      } else {
        console.error('Erro ao buscar dados da conta:', error);
        showModal('error', 'Erro ao buscar dados da conta.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      // navigate('/');
      return;
    }
    if (!cnpj || !numConta) return;
    buscarConta();
  }, [cnpj, numConta]);

  const associarPrograma = async (e) => {
    e.preventDefault();
    if (!newIdPrograma) {
      showModal('error', 'Selecione um programa.');
      return;
    }
    setSaving(true);
    try {
      const body = { idPrograma: parseInt(newIdPrograma, 10) };
      if (newDataInicio) body.dataInicio = newDataInicio;
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL.CONTA_PROGRAMA}/${cnpj}/${numConta}/associar`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showModal('success', 'Programa associado com sucesso!');
      setNewIdPrograma('');
      setNewDataInicio('');
      await buscarConta();
    } catch (error) {
      console.error('Erro ao associar programa:', error);
      showModal('error', 'Erro ao associar programa.');
    } finally {
      setSaving(false);
    }
  };

  const encerrarPrograma = async (idPrograma) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL.CONTA_PROGRAMA}/${cnpj}/${numConta}/atual`,
        { headers: { Authorization: `Bearer ${token}` }, params: { idPrograma } }
      );
      showModal('success', 'Programa encerrado com sucesso!');
      await buscarConta();
    } catch (error) {
      console.error('Erro ao encerrar programa:', error);
      showModal('error', 'Erro ao encerrar programa.');
    } finally {
      setSaving(false);
    }
  };

  const iniciarEdicao = (p) => {
    setEditingPrograma(p.idPrograma);
    setEditDataInicio(p.dataInicio || '');
  };

  const cancelarEdicao = () => {
    setEditingPrograma(null);
    setEditDataInicio('');
  };

  const salvarDataInicio = async (idPrograma) => {
    if (!editDataInicio) {
      showModal('error', 'Informe a data de início.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL.CONTA_PROGRAMA}/${cnpj}/${numConta}/atual`,
        { idPrograma, dataInicio: editDataInicio },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showModal('success', 'Data de início atualizada com sucesso!');
      cancelarEdicao();
      await buscarConta();
    } catch (error) {
      console.error('Erro ao atualizar data de início:', error);
      showModal(
        'error',
        typeof error.response?.data === 'string'
          ? error.response.data
          : 'Erro ao atualizar data de início.'
      );
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    if (d.includes('-')) {
      const [y, m, day] = d.split('-');
      return `${day}/${m}/${y}`;
    }
    return d;
  };

  if (loading) {
    return (
      <PageCard title="Gerenciar Conta-Programa" icon="bi-gear">
        <LoadingModal loading={true} />
      </PageCard>
    );
  }

  return (
    <PageCard title="Gerenciar Conta-Programa" icon="bi-gear">
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {conta && (
          <>
            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-secondary text-white py-2">
                <i className="bi bi-bank me-2"></i>Dados da Conta
              </div>
              <div className="card-body">
                <div className="row small">
                  <div className="col-md-3"><strong>Conta:</strong> {conta.numConta}</div>
                  <div className="col-md-3"><strong>Banco:</strong> {conta.nomeBanco}</div>
                  <div className="col-md-3"><strong>Agência:</strong> {conta.agencia}</div>
                  <div className="col-md-3"><strong>Descrição:</strong> {conta.descricao}</div>
                </div>
              </div>
            </div>

            <div className="card mb-3 shadow-sm">
              <div className={`card-header py-2 text-white ${programasAtivos.length > 0 ? 'bg-success' : 'bg-secondary'}`}>
                <i className="bi bi-check-circle me-2"></i>Programas Ativos
              </div>
              <div className="card-body">
                {programasAtivos.length > 0 ? (
                  programasAtivos.map((p, idx) => (
                    <div key={idx} className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                      <div className="small">
                        <strong>Programa:</strong> {p.nomePrograma}<br />
                        {editingPrograma === p.idPrograma ? (
                          <>
                            <strong>Início:</strong>{' '}
                            <input
                              type="date"
                              className="form-control form-control-sm d-inline-block"
                              style={{ width: 'auto' }}
                              value={editDataInicio}
                              onChange={e => setEditDataInicio(e.target.value)}
                              disabled={saving}
                            />
                          </>
                        ) : (
                          <>
                            <strong>Início:</strong> {formatDate(p.dataInicio)}<br />
                            <strong>Status:</strong>{' '}
                            <span className="badge bg-success">{p.status}</span>
                          </>
                        )}
                      </div>
                      {editingPrograma === p.idPrograma ? (
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => salvarDataInicio(p.idPrograma)}
                            disabled={saving}
                            title="Salvar data de início"
                          >
                            <i className="bi bi-check-lg me-1"></i>Salvar
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={cancelarEdicao}
                            disabled={saving}
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => iniciarEdicao(p)}
                            disabled={saving}
                            title="Editar data de início"
                          >
                            <i className="bi bi-pencil me-1"></i>Editar
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => encerrarPrograma(p.idPrograma)}
                            disabled={saving}
                            title="Encerrar este programa"
                          >
                            <i className="bi bi-x-circle me-1"></i>Encerrar
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-muted small">Nenhum programa associado no momento.</div>
                )}
              </div>
            </div>

            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-primary text-white py-2">
                <i className="bi bi-link-45deg me-2"></i>Associar Novo Programa
              </div>
              <div className="card-body">
                <form onSubmit={associarPrograma} className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small">Programa</label>
                    <ProgramaSelector
                      value={newIdPrograma}
                      onChange={setNewIdPrograma}
                      placeholder="Selecione um programa"
                      disabled={saving}
                      small
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Data de Início</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={newDataInicio}
                      onChange={e => setNewDataInicio(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button type="submit" className="btn btn-success btn-sm w-100" disabled={saving}>
                      <i className="bi bi-plus-lg me-1"></i>Associar
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="card shadow-sm">
              <div className="card-header bg-info text-white py-2">
                <i className="bi bi-clock-history me-2"></i>Histórico de Associações
              </div>
              <div className="card-body p-0">
                {historico.length === 0 ? (
                  <div className="p-3 text-muted small">Nenhum histórico encontrado.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-striped mb-0 small">
                      <thead className="table-light">
                        <tr>
                          <th>Programa</th>
                          <th>Data Início</th>
                          <th>Data Fim</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historico.map((h, idx) => (
                          <tr key={idx}>
                            <td>{h.nomePrograma}</td>
                            <td>{formatDate(h.dataInicio)}</td>
                            <td>{formatDate(h.dataFim)}</td>
                            <td>
                              <span className={`badge ${h.status === 'ATIVO' ? 'bg-success' : 'bg-secondary'}`}>
                                {h.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeModal}
        message={modalData.message}
      />
    </PageCard>
  );
}

export default ContaProgramaManagement;
